import {
  useMemo,
  type DragEvent,
} from 'react'

import {
  extensionRegistry,
} from '../extensions/registry'

import {
  useLayout,
} from '../preferences/LayoutContext'

import {
  ContextualSurface,
} from '../contextual/ContextualSurface'

import type {
  PanelLayout,
  PanelSide,
} from '../preferences/preferences'

function ModuleOptions({
  moduleId,
}: {
  moduleId: string
}) {
  const {
    preferences,
    update,
  } =
    useLayout()

  if (
    moduleId ===
    'system-vps'
  ) {
    return (
      <div className="r7-special-options">

        <strong>
          Sistema / VPS
        </strong>

        <label>
          <span>
            Distribución de botones
          </span>

          <select
            value={
              preferences
                .systemButtonColumns
            }
            onChange={
              event =>
                update({
                  systemButtonColumns:
                    Number(
                      event.target.value,
                    ) as 1 | 2 | 3,
                })
            }
          >
            <option value="1">
              1 columna
            </option>

            <option value="2">
              2 columnas
            </option>

            <option value="3">
              3 columnas
            </option>
          </select>
        </label>

        <label className="r7-range">
          <span>
            Texto de botones

            <strong>
              {Math.round(
                preferences
                  .systemButtonTextScale *
                  100,
              )}%
            </strong>
          </span>

          <input
            type="range"
            min=".70"
            max="1.60"
            step=".02"
            value={
              preferences
                .systemButtonTextScale
            }
            onChange={
              event =>
                update({
                  systemButtonTextScale:
                    Number(
                      event.target.value,
                    ),
                })
            }
          />
        </label>

      </div>
    )
  }

  if (
    moduleId ===
    'engineering'
  ) {
    return (
      <div className="r7-special-options">

        <strong>
          Ingeniería
        </strong>

        <label className="r7-range">
          <span>
            Texto de etapas

            <strong>
              {Math.round(
                preferences
                  .engineeringTextScale *
                  100,
              )}%
            </strong>
          </span>

          <input
            type="range"
            min=".70"
            max="1.60"
            step=".02"
            value={
              preferences
                .engineeringTextScale
            }
            onChange={
              event =>
                update({
                  engineeringTextScale:
                    Number(
                      event.target.value,
                    ),
                })
            }
          />
        </label>

      </div>
    )
  }

  return null
}

export function ExtensionPanels({
  side,
}: {
  side: PanelSide
}) {
  const {
    preferences,
    update,
  } =
    useLayout()

  const modules =
    useMemo(
      () =>
        extensionRegistry
          .modules(),
      [],
    )

  const placements =
    useMemo<PanelLayout[]>(() => {
      return modules.map(
        (
          module,
          index,
        ): PanelLayout => {
          const stored =
            preferences.panels.find(
              item =>
                item.id ===
                module.id,
            )

          if (stored) {
            return stored
          }

          const defaultSide:
          PanelSide =
            module.placement ===
            'RIGHT'
              ? 'RIGHT'
              : 'LEFT'

          return {
            id:
              module.id,

            side:
              defaultSide,

            order:
              index * 10,

            visible:
              true,
          }
        },
      )
    }, [
      modules,
      preferences.panels,
    ])

  function persist(
    next: PanelLayout[],
  ) {
    update({
      panels: next,
    })
  }

  function handleDrop(
    targetId: string | null,
    event:
      DragEvent<
        HTMLElement |
        HTMLDivElement
      >,
  ) {
    event.preventDefault()

    if (
      !preferences.designMode ||
      preferences.designLocked
    ) {
      return
    }

    const draggedId =
      event.dataTransfer.getData(
        'text/genesis-panel',
      )

    if (!draggedId) {
      return
    }

    const next:
      PanelLayout[] =
        placements.map(
          item => ({
            ...item,
          }),
        )

    const dragged =
      next.find(
        item =>
          item.id === draggedId,
      )

    if (!dragged) {
      return
    }

    dragged.side =
      side

    const sameSide =
      next
        .filter(
          item =>
            item.side === side &&
            item.id !== draggedId,
        )
        .sort(
          (a, b) =>
            a.order -
            b.order,
        )

    if (targetId) {
      const targetIndex =
        sameSide.findIndex(
          item =>
            item.id === targetId,
        )

      sameSide.splice(
        targetIndex < 0
          ? sameSide.length
          : targetIndex,
        0,
        dragged,
      )
    } else {
      sameSide.push(
        dragged,
      )
    }

    sameSide.forEach(
      (
        item,
        index,
      ) => {
        item.order =
          index * 10
      },
    )

    persist(next)
  }

  return (
    <aside
      className="side-stack"
      onDragOver={
        event =>
          event.preventDefault()
      }
      onDrop={
        event =>
          handleDrop(
            null,
            event,
          )
      }
    >

      {placements
        .filter(
          item =>
            item.side === side &&
            item.visible,
        )
        .sort(
          (a, b) =>
            a.order -
            b.order,
        )
        .map(
          placement => {
            const manifest =
              modules.find(
                module =>
                  module.id ===
                  placement.id,
              )

            if (!manifest) {
              return null
            }

            const Component =
              manifest.component

            const movable =
              preferences.designMode &&
              !preferences.designLocked

            return (
              <div
                key={
                  manifest.id
                }
                className={
                  movable
                    ? 'extension-panel-shell draggable'
                    : 'extension-panel-shell'
                }
                draggable={
                  movable
                }
                onDragStart={
                  event => {
                    if (!movable) {
                      event.preventDefault()
                      return
                    }

                    event.dataTransfer.setData(
                      'text/genesis-panel',
                      manifest.id,
                    )

                    event.dataTransfer.effectAllowed =
                      'move'
                  }
                }
                onDragOver={
                  event =>
                    event.preventDefault()
                }
                onDrop={
                  event => {
                    event.stopPropagation()

                    handleDrop(
                      manifest.id,
                      event,
                    )
                  }
                }
              >

                {movable && (
                  <div className="drag-handle">
                    ⋮⋮ Arrastrar módulo
                  </div>
                )}

                <ContextualSurface
                  id={
                    `module:${manifest.id}`
                  }
                  title={
                    manifest.name
                  }
                  menuExtra={
                    <ModuleOptions
                      moduleId={
                        manifest.id
                      }
                    />
                  }
                >
                  <Component />
                </ContextualSurface>

              </div>
            )
          },
        )}

    </aside>
  )
}
